import PropTypes from 'prop-types';
import { forwardRef, useState, useEffect, useRef, useCallback } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
// third party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';

// project imports
import { ThemeMode } from 'config';
import Transitions from 'ui-component/extended/Transitions';
import GlobalSearchComponent from './GlobalSearchComponent';

// third party - localization
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

// assets
import { IconSearch, IconX } from '@tabler/icons-react';
import { OPEN_GLOBAL_SEARCH_SSID_HANDOFF, GLOBAL_SEARCH_SSID_SELECT_EVENT, resetGlobalSearchFromSsidPicker } from 'utils/CommonMethods';
import { isAnySsidPage } from './featureSearchData';
import { sanitizeSearchInput } from './searchInput';

function HeaderAvatarComponent({ children, ...others }, ref) {
  const theme = useTheme();

  return (
    <Avatar
      ref={ref}
      variant="rounded"
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'secondary.light',
        color: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.dark',
        '&:hover': {
          bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.dark',
          color: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.light'
        }
      }}
      {...others}
    >
      {children}
    </Avatar>
  );
}

const HeaderAvatar = forwardRef(HeaderAvatarComponent);

// ==============================|| GLOBAL SEARCH - DESKTOP ||============================== //

function DesktopSearch() {
  const theme = useTheme();
  const intl = useIntl();
  const location = useLocation();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isAutoOpenedPopup, setIsAutoOpenedPopup] = useState(false);
  const anchorRef = useRef(null);

  // Close desktop search when viewport resizes to mobile
  useEffect(() => {
    if (isMobileView && open) {
      setOpen(false);
      setSearchValue('');
    }
  }, [isMobileView, open]);

  const handleToggle = (event) => {
    if (open && isGlobalSearchTourStepActive() && event?.isTrusted) {
      return;
    }

    setOpen((prevOpen) => !prevOpen);
  };

  const isGlobalSearchTourStepActive = () => {
    try {
      return sessionStorage.getItem('dashboardTourRunning') === '1' && sessionStorage.getItem('dashboardTourStep') === '1';
    } catch (_) {
      return false;
    }
  };
  const resetSearchIfOnSsidPage = useCallback(() => {
    if (!isAnySsidPage(location.pathname)) return;
    resetGlobalSearchFromSsidPicker();
    setSearchValue('');
  }, [location.pathname]);

  const handleClose = (event) => {
    if (anchorRef.current && event && anchorRef.current.contains(event.target)) {
      return;
    }

    if (isGlobalSearchTourStepActive()) {
      return;
    }
    if (isAnySsidPage(location.pathname)) {
      resetGlobalSearchFromSsidPicker();
    }
    setOpen(false);
    setSearchValue('');
    setIsAutoOpenedPopup(false);
  };

  const handleSearchChange = (value) => {
    const sanitizedValue = sanitizeSearchInput(value);
    setSearchValue(sanitizedValue);
  };

  const handleHeaderClick = () => {
    if (!open) {
      resetSearchIfOnSsidPage();
      setIsAutoOpenedPopup(false);
      setOpen(true);
      // Push state to history so back button can close the modal
      // This creates a history entry that will be popped when back is pressed
      window.history.pushState({ searchModalOpen: true }, '');
    }
  };

  // Handle browser back/forward navigation - close modal when back is pressed
  useEffect(() => {
    const handlePopState = () => {
      // If modal is open when back button is pressed, close it
      if (open) {
        if (isAnySsidPage(location.pathname)) {
          resetGlobalSearchFromSsidPicker();
        }
        setOpen(false);
        setSearchValue('');
        setIsAutoOpenedPopup(false);

        // Check if this was our modal state or actual navigation
        // If event.state is null or doesn't have our flag, it's real navigation
        // The browser will handle navigation automatically - we just close the modal
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open, location.pathname]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Clean up on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const handleSsidHandoffOpen = (event) => {
      const query = event?.detail?.query || '';
      if (query) {
        setSearchValue(query);
      }
      setIsAutoOpenedPopup(true);
      if (!open) {
        setOpen(true);
        window.history.pushState({ searchModalOpen: true }, '');
      }
    };

    const handleSsidSelect = () => {
      setIsAutoOpenedPopup(false);
    };

    window.addEventListener(OPEN_GLOBAL_SEARCH_SSID_HANDOFF, handleSsidHandoffOpen);
    window.addEventListener(GLOBAL_SEARCH_SSID_SELECT_EVENT, handleSsidSelect);
    return () => {
      window.removeEventListener(OPEN_GLOBAL_SEARCH_SSID_HANDOFF, handleSsidHandoffOpen);
      window.removeEventListener(GLOBAL_SEARCH_SSID_SELECT_EVENT, handleSsidSelect);
    };
  }, [open]);

  return (
    <>
      {/* Non-editable Search Field with Placeholder */}
      <Box
        ref={anchorRef}
        onClick={handleHeaderClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 280,
          width: { lg: 335 },
          maxWidth: 'min(420px, 100%)',
          px: 2,
          py: 1,
          height: 34,
          borderRadius: '10px',
          border: `1px solid ${theme.palette.grey[300]}`,
          bgcolor: 'transparent',
          cursor: 'pointer',
          flexShrink: 0,
          overflow: 'hidden',
          '&:hover': {
            borderColor: theme.palette.grey[400],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.grey[400]
            }
          }
        }}
      >
        <IconSearch
          stroke={1.5}
          size="20px"
          style={{
            color: theme.palette.grey[500],
            marginRight: '8px',
            flexShrink: 0
          }}
        />
        <Typography
          variant="body1"
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            color: theme.palette.grey[500],
            fontWeight: 300,
            flexGrow: 1,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {intl.formatMessage({ id: 'globalSearchPlaceholder' })}
        </Typography>
      </Box>

      {/* Search Dropdown */}
      {open && (
        <Box
          onClick={handleToggle}
          id="search-backdrop"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1199
          }}
        />
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        sx={{
          zIndex: 1300,
          width: { xs: '100%', md: 600 }
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 17]
            }
          },
          {
            name: 'flip',
            options: {
              enabled: true,
              fallbackPlacements: ['top-end', 'bottom-start', 'top-start']
            }
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 16
            }
          }
        ]}
        disablePortal={false}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions type="grow" position="top" {...TransitionProps}>
              <Paper
                id="global-search-popup"
                sx={{
                  bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.800' : 'background.paper',
                  padding: 0.5,
                  minHeight: { xs: 'auto', md: '340px' },
                  maxHeight: 'calc(100vh - 80px)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -5,
                    right: 25,
                    width: 13,
                    height: 13,
                    backgroundColor: 'background.paper',
                    transform: 'rotate(45deg)',
                    zIndex: -1,
                    borderTop: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: 'divider'
                  }}
                />
                {/* Search Input inside Dropdown - sticky so it stays visible when scrolling */}
                <Box
                  sx={{
                    p: '12px 12px 12px 24px',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.800' : 'background.paper'
                  }}
                >
                  <InputBase
                    fullWidth
                    value={searchValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    disabled={isAutoOpenedPopup}
                    startAdornment={
                      <InputAdornment position="start">
                        <IconSearch size="20px" />
                      </InputAdornment>
                    }
                    endAdornment={
                      searchValue &&
                      !isAutoOpenedPopup && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchValue('')}
                            sx={{
                              color: 'grey.800'
                            }}
                          >
                            <IconX size="16px" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    size="small"
                    autoFocus={!isAutoOpenedPopup}
                  />
                </Box>

                {/* Search Results - scrollable so sticky search bar stays visible */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <GlobalSearchComponent
                    searchQuery={searchValue}
                    onClose={handleClose}
                    onSearchChange={setSearchValue}
                    isDropdown={true}
                  />
                </Box>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}

// ==============================|| GLOBAL SEARCH - MOBILE ||============================== //

function MobileSearch({ value, setValue, popupState, searchInputDisabled = false }) {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Box sx={{ p: 2 }}>
      <OutlinedInput
        id="input-search-header-mobile"
        value={value}
        onChange={(e) => setValue(sanitizeSearchInput(e.target.value))}
        disabled={searchInputDisabled}
        placeholder={intl.formatMessage({ id: 'globalSearchPlaceholder' })}
        startAdornment={
          <InputAdornment position="start">
            <IconSearch stroke={2} size="20px" />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <Box sx={{ ml: 2 }}>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.mediumAvatar,
                  bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'orange.light',
                  color: 'orange.dark',
                  '&:hover': {
                    bgcolor: 'orange.dark',
                    color: 'orange.light'
                  }
                }}
                {...bindToggle(popupState)}
              >
                <IconX stroke={1.5} size="20px" />
              </Avatar>
            </Box>
          </InputAdornment>
        }
        aria-describedby="search-helper-text"
        inputProps={{
          'aria-label': 'global-search-mobile',
          sx: {
            bgcolor: 'transparent',
            pl: 0.5,
            color: 'grey.900',
            '&::placeholder': {
              color: 'grey.500',
              opacity: 1,
              fontWeight: 300
            }
          }
        }}
        sx={{
          width: '100%',
          ml: 0.5,
          px: 1.5,
          bgcolor: 'background.paper',
          height: 45
        }}
      />

      <Box sx={{ mt: 2 }}>
        <GlobalSearchComponent searchQuery={value} onClose={popupState.close} onSearchChange={setValue} isMobile={true} />
      </Box>
    </Box>
  );
}

// ==============================|| MAIN SEARCH SECTION ||============================== //

export default function SearchSection() {
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileValue, setMobileValue] = useState('');
  const [isMobilePopupOpen, setIsMobilePopupOpen] = useState(false);
  const [isMobileAutoOpened, setIsMobileAutoOpened] = useState(false);
  const mobilePopupStateRef = useRef(null);
  const wasMobileOpenRef = useRef(false);

  // Close mobile search when viewport resizes to desktop (avoids panel stuck under header)
  useEffect(() => {
    if (isDesktop && isMobilePopupOpen && mobilePopupStateRef.current) {
      mobilePopupStateRef.current.close();
      setMobileValue('');
      setIsMobilePopupOpen(false);
      setIsMobileAutoOpened(false);
      wasMobileOpenRef.current = false;
    }
  }, [isDesktop, isMobilePopupOpen]);

  // Handle browser back/forward navigation for mobile search
  // Close modal when back is pressed, but allow normal navigation to proceed
  useEffect(() => {
    const handlePopState = () => {
      // If modal is open when back button is pressed, close it
      // This doesn't prevent normal navigation - browser will navigate back normally
      if (isMobilePopupOpen && mobilePopupStateRef.current) {
        mobilePopupStateRef.current.close();
        setMobileValue('');
        setIsMobilePopupOpen(false);
        setIsMobileAutoOpened(false);
        wasMobileOpenRef.current = false;
      }
      // Normal browser navigation proceeds automatically - we don't interfere
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMobilePopupOpen]);

  useEffect(() => {
    const handleSsidHandoffOpen = (event) => {
      const query = event?.detail?.query || '';
      if (query) {
        setMobileValue(query);
      }
      setIsMobileAutoOpened(true);
      if (!isMobilePopupOpen && mobilePopupStateRef.current) {
        mobilePopupStateRef.current.open?.();
        setIsMobilePopupOpen(true);
        wasMobileOpenRef.current = true;
        window.history.pushState({ searchModalOpen: true }, '');
      }
    };

    const handleSsidSelect = () => {
      setIsMobileAutoOpened(false);
    };

    window.addEventListener(OPEN_GLOBAL_SEARCH_SSID_HANDOFF, handleSsidHandoffOpen);
    window.addEventListener(GLOBAL_SEARCH_SSID_SELECT_EVENT, handleSsidSelect);
    return () => {
      window.removeEventListener(OPEN_GLOBAL_SEARCH_SSID_HANDOFF, handleSsidHandoffOpen);
      window.removeEventListener(GLOBAL_SEARCH_SSID_SELECT_EVENT, handleSsidSelect);
    };
  }, [isMobilePopupOpen]);

  return (
    <>
      {/* Mobile Search */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <PopupState variant="popper" popupId="global-search-mobile">
          {(popupState) => {
            // Store popupState reference for back button handling
            mobilePopupStateRef.current = popupState;

            // Create a custom toggle handler that tracks state
            const handleMobileToggle = (event) => {
              const wasOpen = wasMobileOpenRef.current;
              bindToggle(popupState).onClick(event);

              // Update ref for next time
              wasMobileOpenRef.current = !wasOpen;

              if (!wasOpen) {
                // Opening the popup - push state so back button can close it
                if (isAnySsidPage(location.pathname)) {
                  resetGlobalSearchFromSsidPicker();
                  setMobileValue('');
                }
                setIsMobileAutoOpened(false);
                setIsMobilePopupOpen(true);
                window.history.pushState({ searchModalOpen: true }, '');
              } else {
                // Closing the popup
                if (isAnySsidPage(location.pathname)) {
                  resetGlobalSearchFromSsidPicker();
                  setMobileValue('');
                }
                setIsMobilePopupOpen(false);
                setIsMobileAutoOpened(false);
              }
            };

            return (
              <>
                <Box sx={{ ml: 2 }}>
                  <HeaderAvatar onClick={handleMobileToggle} sx={{ color: '#fff', backgroundColor: 'transparent' }}>
                    <IconSearch stroke={1.5} size="21.2px" />
                  </HeaderAvatar>
                </Box>
                <Popper
                  {...bindPopper(popupState)}
                  transition
                  sx={{
                    zIndex: 1100,
                    width: '100%',
                    top: { sm: '-43px !important', md: '-40px !important' },
                    px: { xs: 1.25, sm: 1.5 },
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {({ TransitionProps }) => (
                    <>
                      <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'top center' }}>
                        <Card sx={{ border: 0, boxShadow: 'none' }}>
                          <MobileSearch
                            value={mobileValue}
                            setValue={setMobileValue}
                            searchInputDisabled={isMobileAutoOpened}
                            popupState={{
                              ...popupState,
                              close: () => {
                                popupState.close();
                                setIsMobilePopupOpen(false);
                                setIsMobileAutoOpened(false);
                                wasMobileOpenRef.current = false;
                              }
                            }}
                          />
                        </Card>
                      </Transitions>
                    </>
                  )}
                </Popper>
              </>
            );
          }}
        </PopupState>
      </Box>

      {/* Desktop Search */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <DesktopSearch />
      </Box>
    </>
  );
}

// PropTypes
HeaderAvatarComponent.propTypes = {
  children: PropTypes.node,
  others: PropTypes.any
};

MobileSearch.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func,
  popupState: PropTypes.any,
  searchInputDisabled: PropTypes.bool
};
